/**
 * Per-tenant RUNTIME feature flags for the frontend.
 *
 * Unlike `feature-flags.ts` (build-time, Vite env, identical for every tenant of a given
 * build), these flags are resolved by the BACKEND per request, from the tenant's own
 * `.env.<tenant>` file (see `backend/src/Helper/TenantFeatureFlags.php`), and injected as
 * `window.__FEATURE_FLAGS__` into the served HTML shell — the same mechanism that already
 * carries `window.__APP_CONTEXT__` (see `HomeAction.php`). Kept in a separate file from
 * `feature-flags.ts` on purpose: that file's own doc comment states it is NOT a runtime
 * system, and conflating the two would falsify that for every future reader.
 *
 * A boolean flag is enabled only when its key is exactly `true`; any other value (missing,
 * `undefined`, anything else) leaves it OFF — same convention as `feature-flags.ts`; `tenantTheme`
 * is opt-in. Issue types have no switch any more (removed 2026-10-20): they are always on.
 * `boardSortDefault` is the one text value: the backend only ever sends an allowed word, but
 * the reader still validates it (see `tenantBoardSortDefault`).
 */

import { BOARD_SORT_FALLBACK, parseBoardSort, type BoardSort } from './helpers/board-sort.helper';

declare global {
    interface Window {
        // `boardSortDefault` is `false` when HomeAction's json_encode fallback replaced every
        // flag with `false` — hence `string | false`, not `string`.
        __FEATURE_FLAGS__?: { boardSortDefault?: string | false; tenantTheme?: boolean };
    }
}

/**
 * The tenant theme stylesheet switch (`FEATURE_TENANT_THEME_ENABLED`, feature plan 0054): gates the
 * editor route and its menu entry. HomeAction leaves the theme `<link>` out of the page on its own
 * when the switch is off. Kept pure (flags object passed in, not read internally) because
 * `vitest.config.ts` runs with `environment: 'node'`, where `window` does not exist unless a test
 * shims it first.
 */
export function isTenantThemeEnabled(flags: Window['__FEATURE_FLAGS__']): boolean {
    return flags?.tenantTheme === true;
}

/**
 * The tenant's default sort order of the project board (`BOARD_SORT_DEFAULT` in the tenant's
 * `.env.<tenant>`). Pure for the same reason as `isTenantThemeEnabled`. Anything that is not an
 * allowed word — missing key, the `false` fallback, garbage — falls back to `'milestone'`.
 */
export function tenantBoardSortDefault(flags: Window['__FEATURE_FLAGS__']): BoardSort {
    return parseBoardSort(flags?.boardSortDefault) ?? BOARD_SORT_FALLBACK;
}
